import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkStringify from 'remark-stringify';

/** Preserve quote boundaries when pasting through the interactive editor too. */
export function pasteDocumentContent({ event, editor, defaultPasteHandler }: any) {
  const clipboard = event.clipboardData;
  if (!clipboard || clipboard.types.includes('blocknote/html') ||
      clipboard.types.includes('vscode-editor-data') ||
      editor.getTextCursorPosition().block.type === 'codeBlock') return defaultPasteHandler();
  const markdown = clipboard.getData('text/markdown') || clipboard.getData('text/plain');
  const pasteBlocks = async (blocks: any[]) => {
    const html = await editor.blocksToFullHTML(blocks);
    const document = new DOMParser().parseFromString(html, 'text/html');
    // Both wrappers and their semantic children match this version's parse
    // rules. Keep one block node, with its inline content directly inside.
    document.querySelectorAll('div[data-content-type]').forEach(wrapper => {
      const content = wrapper.firstElementChild;
      if (content && /^(P|H[1-6]|BLOCKQUOTE)$/.test(content.tagName)) {
        content.replaceWith(...Array.from(content.childNodes));
      }
    });
    editor.pasteHTML(document.body.innerHTML, true);
  };
  if (/^\s{0,3}>[ \t]?/m.test(markdown)) {
    // Full BlockNote HTML retains the explicitly parsed quote structure. The
    // default Markdown paste goes through lossy HTML and detaches its paragraph.
    void parseExtractedMarkdown(editor, markdown)
      .then(pasteBlocks)
      .catch(() => defaultPasteHandler());
    return true;
  }
  const html = clipboard.getData('text/html');
  if (html && /<blockquote[\s>]/i.test(html)) {
    const document = new DOMParser().parseFromString(html, 'text/html');
    document.querySelectorAll('blockquote').forEach(quote => {
      const first = quote.firstElementChild;
      if (first?.tagName === 'P') first.replaceWith(...Array.from(first.childNodes));
    });
    void editor.tryParseHTMLToBlocks(document.body.innerHTML).then(pasteBlocks).catch(() => defaultPasteHandler());
    return true;
  }
  return defaultPasteHandler();
}

/** Import quote boundaries explicitly: BlockNote's HTML importer expects
 * inline quote content, while Markdown emits blockquote > paragraph. */
export async function parseExtractedMarkdown(editor: any, markdown: string) {
  const processor = unified().use(remarkParse).use(remarkGfm).use(remarkStringify);
  const tree = processor.parse(markdown);
  const convert = async (nodes: any[]): Promise<any[]> => {
    const result: any[] = [];
    let pending: any[] = [];
    const flush = async () => {
      if (!pending.length) return;
      const text = processor.stringify({ type: 'root', children: pending });
      result.push(...await editor.tryParseMarkdownToBlocks(text));
      pending = [];
    };
    for (const node of nodes) {
      if (node.type !== 'blockquote') { pending.push(node); continue; }
      await flush();
      const contents = await convert(node.children);
      const first = contents[0];
      if (first?.type === 'paragraph') {
        result.push({ ...first, type: 'quote', children: [...(first.children || []), ...contents.slice(1)] });
      } else {
        result.push({ type: 'quote', content: [], children: contents });
      }
    }
    await flush();
    return result;
  };
  return convert(tree.children);
}
